/* FABRICA-CARD-QUICK-ADD
 * card-quick-add.js - one-tap add to cart from a product card.
 *
 * Not the theme's own `quick-add.js`, which defines Dawn's <quick-add-modal>.
 * Adjacent name, different feature; this one is prefixed to keep them apart.
 *
 * Deliberately does NOT open the cart drawer. On a collection page the point is
 * to let someone add three bags in three taps; the drawer would interrupt every
 * one of them. The button confirms inline, the header bubble bumps, and the
 * drawer's markup is refreshed in the background so it is already correct when
 * they do open it.
 *
 * It talks to /cart/add.js directly rather than going through product-form.js:
 * that component wants a <form> (invalid inside the card's <a>) and its cart
 * handler always opens the drawer.
 */
(() => {
  const DONE_MS = 2000;
  const ERROR_MS = 3000;

  class CardQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.button = this.querySelector('.qa__btn');
      this.label = this.querySelector('.qa__text');
      this.live = this.querySelector('[data-qa-live]');
      if (!this.button) return;
      this.button.addEventListener('click', this.onClick.bind(this));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    get state() {
      return this.dataset.state;
    }

    set state(value) {
      this.dataset.state = value;
      if (!this.label) return;
      if (value === 'done') this.label.textContent = this.dataset.addedText;
      else if (value === 'error') this.label.textContent = this.dataset.errorText;
      else this.label.textContent = this.label.dataset.qaIdle;
    }

    /** The card is one big <a>. preventDefault stops it navigating. */
    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.state === 'busy') return;
      this.add();
    }

    async add() {
      clearTimeout(this.timer);
      this.state = 'busy';

      const item = { id: Number(this.dataset.variantId), quantity: 1 };
      if (this.dataset.grind) item.properties = { 'Grind Type': this.dataset.grind };

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items: [item],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        // /cart/add.js only carries `status` when it refused the line.
        if (!response.ok || data.status) {
          this.fail(data.description || data.message);
          return;
        }

        this.refreshCart(data.sections);
        this.succeed();
      } catch (error) {
        console.error('card-quick-add', error);
        this.fail();
      }
    }

    succeed() {
      this.state = 'done';
      this.announce(this.dataset.addedText);
      this.timer = setTimeout(() => {
        this.state = 'idle';
      }, DONE_MS);
    }

    fail(message) {
      this.state = 'error';
      this.announce(message || this.dataset.errorText);
      this.timer = setTimeout(() => {
        this.state = 'idle';
      }, ERROR_MS);
    }

    announce(message) {
      if (this.live && message) this.live.textContent = message;
    }

    /**
     * Repaint the header bubble and the (closed) drawer from the section HTML
     * the add call returned, then tell the rest of the theme the cart moved.
     */
    refreshCart(sections) {
      if (!sections) return;

      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && sections['cart-icon-bubble']) {
        // Only the count is lifted out of the section, not the whole bubble:
        // the header paints its own `bg-blue text-black` on that div and
        // wholesale replacement would drop it.
        const fresh = parse(sections['cart-icon-bubble'], '.cart-count-bubble');
        let count = cartIcon.querySelector('.cart-count-bubble');
        if (fresh && !count) {
          count = document.createElement('div');
          count.className = 'cart-count-bubble bg-blue text-black';
          cartIcon.appendChild(count);
        }
        if (fresh && count) {
          count.innerHTML = fresh.innerHTML;
          count.classList.remove('qa-bump');
          void count.offsetWidth; // restart the animation on every add
          count.classList.add('qa-bump');
        }
      }

      const drawerInner = document.getElementById('CartDrawer');
      const freshDrawer = sections['cart-drawer'] && parse(sections['cart-drawer'], '#CartDrawer');
      if (drawerInner && freshDrawer) drawerInner.innerHTML = freshDrawer.innerHTML;

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.remove('is-empty');
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'card-quick-add',
          productVariantId: this.dataset.variantId,
        });
      }
    }
  }

  function parse(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  if (!customElements.get('card-quick-add')) customElements.define('card-quick-add', CardQuickAdd);
})();
